import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Coffee, Sparkles, Share2, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { getPhone, getName } from "../lib/session";
import PhoneShell from "../components/PhoneShell";

/**
 * Coffeein7 Universal Wallet.
 *
 * One customer = one card. Aggregates Beans + café count across every café
 * the customer has visited. Individual café stamp progress is shown below
 * the hero card. Replaces the per-café /:slug/wallet page as the primary
 * wallet route (the old route still works for legacy links — see App.js).
 */
const UniversalWallet = () => {
    const nav = useNavigate();
    const phone = getPhone();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        if (!phone) {
            nav("/me", { replace: true });
            return;
        }
        api.get(`/me/${phone}`)
            .then((r) => setData(r.data))
            .catch((e) => setErr(e?.response?.data?.detail || "Couldn't load wallet"))
            .finally(() => setLoading(false));
    }, [phone, nav]);

    // Cross-café aggregates for the hero card.
    const totals = useMemo(() => {
        if (!data) return { beans: 0, cafes: 0, totalStamps: 0, freeAvailable: 0 };
        return {
            beans: data.cafes.reduce((s, c) => s + (c.beans_balance || 0), 0),
            cafes: data.cafes.length,
            totalStamps: data.cafes.reduce((s, c) => s + (c.stamps || 0), 0),
            freeAvailable: data.cafes.filter((c) => c.free_coffee_available).length,
        };
    }, [data]);

    const share = async () => {
        try {
            await navigator.share({
                title: "My Coffeein7",
                text: `${totals.beans} Beans across ${totals.cafes} café${totals.cafes === 1 ? "" : "s"} ☕`,
                url: "https://coffeein7.com",
            });
        } catch (_) { /* user cancelled */ }
    };

    if (loading) {
        return (
            <PhoneShell>
                <div className="min-h-screen flex items-center justify-center text-[#8C7A72]">
                    Loading your wallet…
                </div>
            </PhoneShell>
        );
    }

    return (
        <PhoneShell>
            <div data-testid="universal-wallet" className="min-h-screen pb-10">
                {/* Header */}
                <div className="px-5 pt-6 pb-4 flex items-center justify-between">
                    <button
                        onClick={() => nav(-1)}
                        aria-label="Back"
                        className="h-10 w-10 rounded-full bg-white border border-[#E8DCCB] flex items-center justify-center"
                    >
                        <ChevronLeft className="h-4 w-4" strokeWidth={2.5} />
                    </button>
                    <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#8C7A72]">
                        Coffeein7 Wallet
                    </div>
                    {typeof navigator !== "undefined" && navigator.share ? (
                        <button
                            onClick={share}
                            aria-label="Share"
                            className="h-10 w-10 rounded-full bg-white border border-[#E8DCCB] flex items-center justify-center"
                        >
                            <Share2 className="h-4 w-4" strokeWidth={2.5} />
                        </button>
                    ) : (
                        <div className="h-10 w-10" />
                    )}
                </div>

                {/* Hero card — ONE universal card, not per café */}
                <motion.div
                    initial={{ y: 12, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ type: "spring", damping: 22, stiffness: 220 }}
                    className="mx-4 rounded-[28px] bg-[#2C1A12] text-white p-6 relative overflow-hidden shadow-[0_20px_50px_rgba(44,26,18,0.35)]"
                    data-testid="wallet-hero"
                >
                    {/* Warm accents */}
                    <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[#FF4F18]/30" />
                    <div className="absolute -left-24 -bottom-24 h-48 w-48 rounded-full bg-[#FF4F18]/10" />

                    <div className="relative">
                        <div className="flex items-baseline justify-between">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">
                                    Member
                                </div>
                                <div className="mt-1 font-display font-black text-2xl leading-tight">
                                    {(getName() || data?.name || "Coffeein7 member").slice(0, 24)}
                                </div>
                                <div className="text-xs text-white/50 mt-1 tracking-wider">
                                    ···{phone?.slice(-4)}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-3xl font-display font-black">
                                    <span className="text-[#FF4F18]">Coffee</span>in7
                                </div>
                                <div className="text-[10px] uppercase tracking-widest text-white/50 mt-0.5">
                                    Good coffee goes further
                                </div>
                            </div>
                        </div>

                        {/* Beans + café count */}
                        <div className="mt-6 grid grid-cols-3 gap-3">
                            <Stat label="Beans" value={totals.beans} tone="orange" />
                            <Stat label="Cafés" value={totals.cafes} />
                            <Stat
                                label="Free ready"
                                value={totals.freeAvailable}
                                tone={totals.freeAvailable > 0 ? "green" : ""}
                            />
                        </div>
                    </div>
                </motion.div>

                <p className="text-xs text-[#8C7A72] text-center mt-3 px-6">
                    One universal wallet across every Coffeein7 café. No separate cards to add.
                </p>

                {/* Per-café loyalty breakdown */}
                <section className="mt-6 px-4">
                    <div className="flex items-baseline justify-between px-1 mb-3">
                        <h2 className="font-display font-black text-xl">Your cafés</h2>
                        <span className="text-xs text-[#8C7A72] uppercase tracking-widest">
                            {data?.cafes?.length || 0} total
                        </span>
                    </div>

                    {err && <div className="text-sm text-[#c73810] px-2">{err}</div>}

                    {data?.cafes?.length === 0 ? (
                        <div className="text-center py-10 text-[#8C7A72] text-sm">
                            No cafés yet. Scan a Coffeein7 QR to add your first.
                        </div>
                    ) : (
                        <div className="flex flex-col gap-2">
                            {data?.cafes?.map((c) => (
                                <CafeRow key={c.tenant.slug} c={c} onOpen={() => nav(`/${c.tenant.slug}`)} />
                            ))}
                        </div>
                    )}
                </section>
            </div>
        </PhoneShell>
    );
};

const Stat = ({ label, value, tone }) => {
    const cls =
        tone === "orange"
            ? "text-[#FF4F18]"
            : tone === "green"
            ? "text-[#1B995E]"
            : "text-white";
    return (
        <div className="rounded-2xl bg-white/[0.06] border border-white/10 px-3 py-3 text-center">
            <div className={`font-display font-black text-2xl ${cls}`}>{value}</div>
            <div className="text-[10px] uppercase tracking-widest text-white/60 mt-0.5">
                {label}
            </div>
        </div>
    );
};

const CafeRow = ({ c, onOpen }) => {
    // We don't know each café's stamps_required here (only the target for the
    // one the customer is viewing). /me/ doesn't include it yet, so we assume
    // the stamps target is inferable from the loyalty state: if stamps=0 &&
    // free_available=true, they just unlocked. Otherwise render 10 as a hint.
    const target = 10;  // TODO Phase B: return per-café target in /me
    const pct = Math.min(100, Math.round((c.stamps / target) * 100));
    return (
        <button
            data-testid={`wallet-cafe-${c.tenant.slug}`}
            onClick={onOpen}
            className="text-left rounded-2xl bg-white border border-[#E8DCCB] p-4 active:scale-[0.98] transition-transform"
        >
            <div className="flex items-baseline justify-between gap-3">
                <div className="min-w-0">
                    <div className="font-display font-bold text-base truncate">
                        {c.tenant.name}
                    </div>
                    <div className="text-xs text-[#8C7A72] mt-0.5">
                        {c.free_coffee_available ? (
                            <span className="text-[#1B995E] font-bold inline-flex items-center gap-1">
                                <CheckCircle2 className="h-3 w-3" strokeWidth={3} />
                                Free coffee ready
                            </span>
                        ) : (
                            <>
                                <Coffee className="inline h-3 w-3 mr-1" strokeWidth={2.5} />
                                {c.stamps} / {target} coffees
                            </>
                        )}
                    </div>
                </div>
                <div className="shrink-0 flex items-center gap-1 text-[#FF4F18] font-display font-bold text-sm">
                    <Sparkles className="h-3.5 w-3.5" strokeWidth={2.5} />
                    {c.beans_balance}
                </div>
            </div>
            {!c.free_coffee_available && (
                <div className="mt-3 h-1.5 rounded-full bg-[#F5EDE0] overflow-hidden">
                    <div
                        className="h-full bg-[#FF4F18]"
                        style={{ width: `${pct}%` }}
                    />
                </div>
            )}
        </button>
    );
};

export default UniversalWallet;
